import type { ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { Button, InputGroup, Loader } from '@systemeio/components'
import { SearchMdIcon, XCloseIcon } from '@systemeio/icons'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'
import { useClickOutside } from 'shared/hooks/use-click-outside'
import { useDebouncedValue } from 'shared/hooks/use-debounced-value'
import { useSearchLectures } from '../api/get-search-lectures'
import { useGetCoursePageUrl } from '../hooks/use-course-path-url'
import type { CourseId } from '../types/course-interface'

export const CourseSearchForm = ({
  courseId,
  className,
}: {
  courseId: CourseId
  className?: string
}): ReactNode => {
  const { t } = useTranslation()
  const router = useRouter()

  const [isOpen, setIsOpen] = useState(false)

  const querySearchValue = router.query.search
  const searchFromQuery = Array.isArray(querySearchValue)
    ? querySearchValue[0] || ''
    : querySearchValue || ''
  const coursePath = useGetCoursePageUrl()
  const form = useForm({
    defaultValues: {
      search: searchFromQuery,
    },
  })
  const search = form.watch('search')
  const [searchValue, _] = useDebouncedValue(search, 500)
  const getSearchLecturesQuery = useSearchLectures({ courseId, search: searchValue })

  useEffect(() => {
    if (!router.isReady) return
    if (form.getValues('search') === searchFromQuery) return

    form.setValue('search', searchFromQuery)
  }, [form, router.isReady, searchFromQuery])

  const handleClickOutside = useCallback(() => setIsOpen(false), [setIsOpen])
  const containerRef = useClickOutside<HTMLDivElement>(handleClickOutside)

  const handleClear = (): void => {
    form.setValue('search', '')
    if (router.query?.search !== undefined) {
      const { search: _removedSearch, ...rest } = router.query
      router.replace({ pathname: router.pathname, query: rest }, undefined, { shallow: true })
    }
    setIsOpen(false)
  }

  if (!coursePath) return null

  return (
    <div ref={containerRef} className={cn('relative w-full min-w-37.5', className)}>
      <InputGroup.Root className="w-full bg-THEME-block-background text-THEME-primary">
        <InputGroup.Input
          data-testid="course-search-form"
          value={search}
          onChange={e => {
            setIsOpen(true)
            form.setValue('search', e.target.value)
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={t('global.search')}
          className="text-THEME-primary placeholder:text-THEME-primary/50"
        />
        <InputGroup.Addon align="inline-end" appearance="subtle">
          {search !== '' ? (
            <Button
              type="button"
              variant="ghost-gray"
              size="icon-xs"
              aria-label={t('global.remove')}
              onClick={handleClear}
            >
              <XCloseIcon aria-hidden="true" className="size-4 text-THEME-primary" />
            </Button>
          ) : (
            <SearchMdIcon aria-hidden="true" className="size-4 text-THEME-primary" />
          )}
        </InputGroup.Addon>
      </InputGroup.Root>

      {isOpen && searchValue && (
        <div className="absolute inset-x-0 z-20 mt-1 w-full overflow-hidden rounded-lg border border-THEME-disabled bg-THEME-block-background text-THEME-primary shadow-lg">
          {getSearchLecturesQuery.error !== undefined ? (
            <div className="px-4 py-2.5 text-sm text-THEME-primary">
              {getSearchLecturesQuery.error?.errors?.fields?.searchBy?.[0] ?? t('global.error')}
            </div>
          ) : getSearchLecturesQuery.isLoading || getSearchLecturesQuery.data === undefined ? (
            <div className="flex justify-center px-4 py-3">
              <Loader />
            </div>
          ) : getSearchLecturesQuery.data.length > 0 ? (
            <ul className="max-h-80 divide-y divide-THEME-disabled overflow-auto">
              {getSearchLecturesQuery.data.map(item => (
                <li key={item.id}>
                  <Link
                    href={{
                      pathname: `/school/course/${coursePath}/lecture/${item.id}`,
                      query: {
                        search: searchValue,
                      },
                    }}
                    prefetch={false}
                    className="block truncate px-4 py-2.5 hover:bg-THEME-primary hover:text-THEME-block-background"
                    onClick={() => {
                      setIsOpen(false)
                    }}
                  >
                    {item.moduleName}: {item.lectureName}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-2.5 text-sm text-THEME-primary">
              {t('global.list.empty_list')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
